import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const TIME_SLOTS = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00',
];

const STATUS_COLOR = {
  scheduled: { bg: '#e8f4fd', color: C },
  completed: { bg: '#e6f4ea', color: '#27ae60' },
  cancelled: { bg: '#fce8e6', color: '#c5221f' },
  'no-show': { bg: '#fef3c7', color: '#e67e22' },
};

const EMPTY = {
  patientId: '', doctorId: '', apptDate: '', apptTime: '',
  type: 'consultation', status: 'scheduled', notes: '',
};

const inp = { className: 'form-control' };

function F({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export default function HospitalAppointments({ role, user }) {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate]           = useState(today);
  const [appointments, setAppts]  = useState([]);
  const [doctors, setDoctors]     = useState([]);
  const [patients, setPatients]   = useState([]);
  const [filterDoc, setFilterDoc] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState(EMPTY);
  const [editId, setEditId]       = useState(null);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');
  const [patSearch, setPatSearch] = useState('');
  const [bookedTimes, setBookedTimes] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const load = useCallback(() => {
    hospitalApi.getAppointments(date).then(d => { if (d) setAppts(d); });
  }, [date]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    hospitalApi.getDoctors().then(d => { if (d) setDoctors(d.filter(doc => doc.status === 'active')); });
    hospitalApi.getPatients().then(d => { if (d) setPatients(d); });
  }, []);

  // Load booked slots when doctor + date are selected in the modal
  useEffect(() => {
    if (!showModal || !form.doctorId || !form.apptDate) { setBookedTimes([]); return; }
    setSlotsLoading(true);
    hospitalApi.getAppointments(form.apptDate)
      .then(appts => {
        if (!appts) { setSlotsLoading(false); return; }
        const times = appts
          .filter(a => String(a.doctor_id) === form.doctorId && a.status !== 'cancelled' && (!editId || a.id !== editId))
          .map(a => a.appt_time)
          .filter(Boolean);
        setBookedTimes(times);
        setSlotsLoading(false);
      })
      .catch(() => setSlotsLoading(false));
  }, [showModal, form.doctorId, form.apptDate, editId]);

  const filtered = appointments.filter(a => !filterDoc || String(a.doctor_id) === filterDoc);

  const resetForm = () => { setForm({ ...EMPTY, apptDate: date }); setEditId(null); setErr(''); setPatSearch(''); setBookedTimes([]); };

  const openModal = () => { resetForm(); setShowModal(true); };

  const startEdit = (a) => {
    setForm({
      patientId: String(a.patient_id || ''),
      doctorId: String(a.doctor_id || ''),
      apptDate: a.appt_date || date,
      apptTime: a.appt_time || '',
      type: a.type || 'consultation',
      status: a.status || 'scheduled',
      notes: a.notes || '',
    });
    setEditId(a.id);
    setShowModal(true);
    setErr('');
    const pat = patients.find(p => p.id === a.patient_id);
    if (pat) setPatSearch(pat.name);
  };

  const handleSave = async () => {
    if (!form.patientId || !form.doctorId || !form.apptDate) {
      setErr('Patient, Doctor, and Date are required');
      return;
    }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, patientId: parseInt(form.patientId), doctorId: parseInt(form.doctorId) };
      if (editId) await hospitalApi.updateAppointment(editId, payload);
      else {
        const res = await hospitalApi.createAppointment(payload);
        if (res && res.error) { setErr(res.error); setSaving(false); return; }
      }
      load();
      setShowModal(false);
      resetForm();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    const appt = appointments.find(a => a.id === id);
    if (!appt) return;
    await hospitalApi.updateAppointment(id, {
      patientId: appt.patient_id, doctorId: appt.doctor_id,
      apptDate: appt.appt_date, apptTime: appt.appt_time,
      type: appt.type, status, notes: appt.notes,
    });
    load();
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this appointment?')) return;
    await hospitalApi.cancelAppointment(id);
    load();
  };

  const filtPats = patients.filter(p =>
    !patSearch || p.name?.toLowerCase().includes(patSearch.toLowerCase()) || p.phone?.includes(patSearch)
  );

  const publicUrl = user?.ownersid
    ? `${window.location.origin}/app?hospitalPublic=${encodeURIComponent(user.ownersid)}`
    : null;

  return (
    <div>
      {/* Public booking link banner */}
      {publicUrl && (
        <div style={{ background: '#e8f4fd', border: '1px solid #b3d7f5', borderRadius: 10,
          padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: DARK, fontWeight: 600 }}>🔗 Public Booking Link:</span>
          <span style={{ fontSize: 12, color: '#5f6368', wordBreak: 'break-all', flex: 1 }}>{publicUrl}</span>
          <button onClick={() => navigator.clipboard?.writeText(publicUrl)}
            style={{ fontSize: 12, padding: '4px 12px', background: C, color: '#fff', border: 'none',
              borderRadius: 6, cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>Copy</button>
          <a href={publicUrl} target="_blank" rel="noreferrer"
            style={{ fontSize: 12, padding: '4px 12px', background: LIGHT, color: C, border: `1px solid ${C}`,
              borderRadius: 6, textDecoration: 'none', fontWeight: 600, flexShrink: 0 }}>Preview</a>
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <select className="form-control" value={filterDoc} onChange={e => setFilterDoc(e.target.value)}>
            <option value="">All Doctors</option>
            {doctors.map(d => <option key={d.id} value={String(d.id)}>Dr. {d.name}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={openModal}>
          + New Appointment
        </button>
      </div>

      {/* Appointments list */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">📅 Appointments for {date}</div>
          <div style={{ fontSize: 13, color: '#5f6368' }}>{filtered.length} appointment{filtered.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9aa0a6', fontSize: 14 }}>
              No appointments for this date
            </div>
          ) : (
            filtered.map((a, i) => {
              const sc = STATUS_COLOR[a.status] || { bg: '#f1f3f4', color: '#5f6368' };
              return (
                <div key={a.id} style={{
                  borderTop: i > 0 ? '1px solid #f1f3f4' : 'none',
                  padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12
                }}>
                  {/* Time bubble */}
                  <div style={{ width: 64, height: 48, background: LIGHT, borderRadius: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 13, color: DARK, flexShrink: 0, textAlign: 'center' }}>
                    {a.appt_time || '—'}
                  </div>
                  {/* Details */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: DARK }}>{a.patient_name || '—'}</div>
                    <div style={{ fontSize: 12, color: '#5f6368', marginTop: 2 }}>
                      Dr. {a.doctor_name || '—'} · {a.type || 'consultation'}
                      {a.notes && <span> · {a.notes}</span>}
                    </div>
                  </div>
                  {/* Status badge */}
                  <span style={{ background: sc.bg, color: sc.color, borderRadius: 8,
                    padding: '4px 12px', fontSize: 12, fontWeight: 700 }}>
                    {a.status}
                  </span>
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {a.status === 'scheduled' && (
                      <>
                        <button style={{ fontSize: 11, padding: '3px 8px', background: '#e6f4ea',
                          color: '#27ae60', border: '1px solid #b7dfbe', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => updateStatus(a.id, 'completed')}>✓ Done</button>
                        <button style={{ fontSize: 11, padding: '3px 8px', background: '#fef3c7',
                          color: '#e67e22', border: '1px solid #fcd34d', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => updateStatus(a.id, 'no-show')}>No-show</button>
                      </>
                    )}
                    <button style={{ fontSize: 11, padding: '3px 8px', background: LIGHT,
                      color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => startEdit(a)}>Edit</button>
                    {a.status !== 'cancelled' && (
                      <button style={{ fontSize: 11, padding: '3px 8px', background: '#fce8e6',
                        color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                        onClick={() => handleCancel(a.id)}>Cancel</button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 500, maxHeight: '90vh',
            overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 17, color: DARK }}>
                {editId ? 'Edit Appointment' : 'New Appointment'}
              </div>
              <button style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#5f6368' }}
                onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>
            {err && <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠️ {err}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Patient *</label>
                <input {...inp} placeholder="Search patient name or phone…"
                  value={patSearch}
                  onChange={e => { setPatSearch(e.target.value); setForm(f => ({ ...f, patientId: '' })); }} />
                {patSearch && !form.patientId && (
                  <div style={{ border: '1px solid #e8eaed', borderRadius: 6, maxHeight: 150, overflowY: 'auto',
                    background: '#fff', zIndex: 10, position: 'relative' }}>
                    {filtPats.slice(0, 8).map(p => (
                      <div key={p.id}
                        onClick={() => { setForm(f => ({ ...f, patientId: String(p.id) })); setPatSearch(p.name); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                          borderBottom: '1px solid #f1f3f4' }}
                        onMouseEnter={e => e.currentTarget.style.background = LIGHT}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        {p.name} — {p.phone || 'No phone'}
                      </div>
                    ))}
                    {filtPats.length === 0 && <div style={{ padding: 10, color: '#9aa0a6', fontSize: 13 }}>No patients found</div>}
                  </div>
                )}
              </div>

              <F label="Doctor *">
                <select {...inp} value={form.doctorId} onChange={e => setForm(f => ({ ...f, doctorId: e.target.value, apptTime: '' }))}>
                  <option value="">Select Doctor</option>
                  {doctors.map(d => <option key={d.id} value={String(d.id)}>Dr. {d.name} ({d.specialization || 'General'})</option>)}
                </select>
              </F>
              <F label="Date *">
                <input {...inp} type="date" value={form.apptDate} onChange={e => setForm(f => ({ ...f, apptDate: e.target.value, apptTime: '' }))} />
              </F>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Time Slot{form.doctorId && form.apptDate ? '' : ' (select doctor + date first)'}</label>
                {form.doctorId && form.apptDate ? (
                  slotsLoading ? (
                    <div style={{ fontSize: 13, color: '#5f6368', padding: '6px 0' }}>Loading slots…</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                      {TIME_SLOTS.map(t => {
                        const busy = bookedTimes.includes(t);
                        const sel = form.apptTime === t;
                        return (
                          <button key={t} type="button" disabled={busy}
                            onClick={() => setForm(f => ({ ...f, apptTime: sel ? '' : t }))}
                            style={{ padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                              cursor: busy ? 'not-allowed' : 'pointer',
                              background: busy ? '#f1f3f4' : sel ? C : LIGHT,
                              color: busy ? '#bbb' : sel ? '#fff' : C,
                              border: sel ? `1.5px solid ${C}` : '1.5px solid #e8eaed',
                              textDecoration: busy ? 'line-through' : 'none' }}>
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : (
                  <input {...inp} type="time" value={form.apptTime}
                    onChange={e => setForm(f => ({ ...f, apptTime: e.target.value }))} />
                )}
              </div>
              <F label="Type">
                <select {...inp} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="consultation">Consultation</option>
                  <option value="follow-up">Follow-up</option>
                  <option value="emergency">Emergency</option>
                </select>
              </F>
              {editId && (
                <F label="Status">
                  <select {...inp} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no-show">No-show</option>
                  </select>
                </F>
              )}
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Notes</label>
                <textarea {...inp} rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ resize: 'vertical' }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? '💾 Update' : '➕ Book Appointment'}
              </button>
              <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
                background: '#fff', cursor: 'pointer', fontSize: 13 }}
                onClick={() => { setShowModal(false); resetForm(); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
